import React, { useState, useEffect } from 'react';
import { Box, Typography, FormGroup, FormControlLabel, Checkbox, Button, Select, MenuItem, InputLabel, FormControl, Paper, Alert } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { updateUserAvailability } from '../services/userService';
import { recalculateAndStoreBestDay } from '../services/scheduleService'; // Import the recalculation function

const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const SchedulingPrefs = () => {
  const { firestoreUser } = useAuth();
  const [availableDays, setAvailableDays] = useState([]);
  const [vetoDay, setVetoDay] = useState('');
  const [preferredDays, setPreferredDays] = useState([]); // Added for preferred days
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (firestoreUser) {
      setAvailableDays(firestoreUser.availableDays || []);
      setVetoDay(firestoreUser.vetoDay || '');
      setPreferredDays(firestoreUser.preferredDays || []); // Initialize preferred days
    }
  }, [firestoreUser]);

  const handleDayChange = (event) => {
    const { name, checked } = event.target;
    setAvailableDays(prev => 
      checked ? [...prev, name] : prev.filter(day => day !== name)
    );
  };

  const handleVetoChange = (event) => {
    setVetoDay(event.target.value);
  };

  // Handler for preferred day selection
  const handlePreferredDayChange = (rank, event) => {
    const dayToSet = event.target.value; // The day selected in the dropdown
    setPreferredDays(prev => {
      const newPreferences = [...prev]; // Create a mutable copy

      // Find if the dayToSet was previously selected in a different rank
      const previousIndexOfDayToSet = newPreferences.indexOf(dayToSet);

      // Clear the new rank's previous selection if it's different from dayToSet
      // and also clear the dayToSet from its old position if it's being moved
      
      // If dayToSet was selected in another rank, clear that old spot
      if (previousIndexOfDayToSet !== -1 && previousIndexOfDayToSet !== (rank - 1)) {
        newPreferences[previousIndexOfDayToSet] = undefined; // or null, to signify empty
      }

      // Set the new day for the current rank
      if (dayToSet) {
        newPreferences[rank - 1] = dayToSet;
      } else {
        // If "None" was selected, clear this rank
        newPreferences[rank - 1] = undefined; // or null
      }
      
      // Ensure the array doesn't have more than 3 elements and remove trailing undefined/null
      const finalPreferences = newPreferences.slice(0, 3);
      // To truly compact, filter out undefined/null if you always want a dense array,
      // but for fixed ranks, it's often better to keep undefined/null to represent empty slots.
      // For this logic, we'll keep them to represent specific ranks being empty.
      return finalPreferences;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Validate that preferred days are within available days (optional, but good practice)
    const validPreferredDays = preferredDays.filter(day => availableDays.includes(day) && day !== vetoDay);
    if (preferredDays.length > 0 && validPreferredDays.length !== preferredDays.length) {
        setError('One or more of your preferred days are not in your available days list or is your veto day. Please adjust.');
        setLoading(false);
        return;
    }

    try {
      await updateUserAvailability(firestoreUser.uid, availableDays, vetoDay || null, validPreferredDays);
      // After individual preferences are saved, recalculate and store the best day for the campaign
      await recalculateAndStoreBestDay(); 
      setSuccess('Your scheduling preferences have been updated and the campaign schedule has been re-evaluated!');
    } catch (err) {
      console.error("Failed to update preferences or recalculate schedule:", err);
      setError(err.message || 'Failed to update preferences or recalculate schedule. Please try again.');
    }
    setLoading(false);
  };

  return (
    <Paper elevation={3} sx={{ m: 'auto', mt: 4, p: 3, maxWidth: 600, bgcolor: '#333', color: '#fff' }}>
      <Typography variant="h5" component="h2" gutterBottom sx={{ color: '#d98d2a', fontFamily: '"Georgia", serif' }}>
        Set Your D&D Availability
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <Box component="form" onSubmit={handleSubmit}>
        <Typography variant="subtitle1" sx={{ color: '#F5F5F5', mt: 2, mb: 1, fontFamily: '"Georgia", serif' }}>
          Which days are you generally available?
        </Typography>
        <FormGroup sx={{ mb: 2 }}>
          {daysOfWeek.map(day => (
            <FormControlLabel
              key={day}
              control={<Checkbox checked={availableDays.includes(day)} onChange={handleDayChange} name={day} sx={{color: '#d98d2a', '&.Mui-checked': {color: '#d98d2a'}}} />}
              label={day}
              sx={{color: '#F5F5F5'}}
            />
          ))}
        </FormGroup>

        <FormControl fullWidth margin="normal" sx={{ mb: 3 }}>
          <InputLabel id="veto-day-label" sx={{ color: '#d98d2a' }}>Veto One Day (Optional)</InputLabel>
          <Select
            labelId="veto-day-label"
            value={vetoDay}
            label="Veto One Day (Optional)"
            onChange={handleVetoChange}
            sx={{ color: '#F5F5F5', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d98d2a' }, '& .MuiSvgIcon-root': { color: '#d98d2a' } }}
          >
            <MenuItem value=""><em>No Veto</em></MenuItem>
            {daysOfWeek.map(day => (
              <MenuItem key={day} value={day} disabled={availableDays.includes(day) && availableDays.length === 1 && availableDays[0] === day}>
                {day}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="subtitle1" sx={{ color: '#F5F5F5', mt: 3, mb: 1, fontFamily: '"Georgia", serif' }}>
          Rank your top 3 preferred days (Optional):
        </Typography>
        {[1, 2, 3].map(rank => {
          // Get the currently selected day for this rank
          const currentSelectionForRank = preferredDays[rank - 1] || '';
          
          return (
            <FormControl fullWidth margin="normal" key={rank} sx={{ mb: 2 }}>
              <InputLabel id={`preferred-day-${rank}-label`} sx={{ color: '#d98d2a' }}>{`Preference #${rank}`}</InputLabel>
              <Select
                labelId={`preferred-day-${rank}-label`}
                value={currentSelectionForRank}
                label={`Preference #${rank}`}
                onChange={(e) => handlePreferredDayChange(rank, e)}
                sx={{ color: '#F5F5F5', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d98d2a' }, '& .MuiSvgIcon-root': { color: '#d98d2a' } }}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {daysOfWeek
                  .filter(day => {
                    // Day must be available
                    if (!availableDays.includes(day)) return false;
                    // Day must not be the veto day
                    if (day === vetoDay) return false;
                    // Day can be the current selection for this rank OR not selected in other ranks
                    const isSelectedInOtherRank = preferredDays.some((pd, index) => pd === day && index !== (rank - 1));
                    return currentSelectionForRank === day || !isSelectedInOtherRank;
                  })
                  .map(day => (
                    <MenuItem key={day} value={day}>
                      {day}
                    </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        })}

        <Button type="submit" variant="contained" disabled={loading} sx={{ backgroundColor: '#d98d2a', color: '#1c1c1c', '&:hover': { backgroundColor: '#c87d26' }, mt: 2 }}>
          {loading ? 'Saving...' : 'Save Preferences'}
        </Button>
      </Box>
    </Paper>
  );
};

export default SchedulingPrefs;
